/**
 * @fileoverview Cross-Currency Basis Stress Signal
 * Computes stress score from cross-currency basis swap spreads.
 * @module signals/liquidity/cross_currency_basis_stress
 *
 * CONTRACT:
 * - Input: { series: TimeseriesPoint[] } where values are basis points (bps)
 * - Output: BasisStressOutput with stressScore (0-100), severity, avgDeviation, currentBasis
 * - Errors: Throws if series is invalid (empty, malformed, or contains NaN)
 * - Behavior: Pure function, deterministic output for same input
 *
 * DOMAIN CONTEXT:
 * Cross-currency basis swaps measure the cost of borrowing USD via FX swaps.
 * Negative basis = premium for dollar funding = dollar shortage stress.
 * -50bps or worse is considered extreme stress (score 100).
 * 0bps or positive is no stress (score 0).
 */

import { isValidSeries, createSignalValidationError } from "../types.js";

/**
 * Input for the basis stress signal.
 * @typedef {Object} BasisStressInput
 * @property {import('../types.js').TimeseriesPoint[]} series - Basis swap spread series in bps (negative = dollar shortage)
 */

/**
 * Output from the basis stress signal.
 * @typedef {Object} BasisStressOutput
 * @property {number} stressScore - Stress score from 0 to 100
 * @property {'low' | 'medium' | 'high' | 'extreme'} severity - Severity classification
 * @property {number} avgDeviation - Average deviation from zero (rounded to 2 decimals)
 * @property {number} currentBasis - Most recent basis value (rounded to 2 decimals)
 */

/**
 * Threshold for extreme stress in basis points.
 * @type {number}
 */
const EXTREME_STRESS_THRESHOLD_BPS = -50;

/**
 * Severity thresholds for stress score.
 * @type {Object}
 */
const SEVERITY_THRESHOLDS = {
  extreme: 75,
  high: 50,
  medium: 25,
};

/**
 * Determines severity level from stress score.
 * @param {number} stressScore - Stress score 0-100
 * @returns {'low' | 'medium' | 'high' | 'extreme'} Severity level
 */
function determineSeverity(stressScore) {
  if (stressScore >= SEVERITY_THRESHOLDS.extreme) return "extreme";
  if (stressScore >= SEVERITY_THRESHOLDS.high) return "high";
  if (stressScore >= SEVERITY_THRESHOLDS.medium) return "medium";
  return "low";
}

/**
 * Maps basis deviation to stress score.
 * Uses linear interpolation between 0bps (score 0) and -50bps (score 100).
 *
 * @param {number} avgDeviation - Average deviation (typically negative in stress)
 * @returns {number} Stress score 0-100
 */
function deviationToStressScore(avgDeviation) {
  // Positive or zero basis = no stress
  if (avgDeviation >= 0) return 0;

  // At or beyond extreme threshold = maximum stress
  if (avgDeviation <= EXTREME_STRESS_THRESHOLD_BPS) return 100;

  // Linear interpolation between 0 and threshold
  const score = (avgDeviation / EXTREME_STRESS_THRESHOLD_BPS) * 100;
  return Math.round(Math.min(100, Math.max(0, score)));
}

/**
 * Cross-Currency Basis Stress Signal Definition.
 *
 * Measures USD funding stress through cross-currency basis swap spreads.
 * Negative basis indicates a premium for dollar funding, signaling dollar shortage.
 *
 * @type {import('../types.js').SignalDefinition<BasisStressInput, BasisStressOutput>}
 */
export const crossCurrencyBasisStressSignal = {
  id: "cross_currency_basis_stress",
  name: "Cross-Currency Basis Stress",
  description:
    "Measures USD funding stress through cross-currency basis swap spreads. " +
    "Negative basis indicates a premium for dollar funding. " +
    "Returns a stress score (0-100) and severity classification.",
  category: "liquidity",

  /**
   * Computes basis stress from time series.
   *
   * @param {BasisStressInput} input - Input containing basis series
   * @returns {BasisStressOutput} Computed stress output
   * @throws {Error} If series is invalid (empty, malformed, or contains NaN values)
   *
   * @example
   * const result = crossCurrencyBasisStressSignal.compute({
   *   series: [
   *     { t: "2024-01-01", v: -10 },
   *     { t: "2024-12-31", v: -30 }
   *   ]
   * });
   * // result: { stressScore: 40, severity: "medium", avgDeviation: -20, currentBasis: -30 }
   */
  compute(input) {
    const { series } = input;

    if (!isValidSeries(series)) {
      throw new Error(
        createSignalValidationError(
          "cross_currency_basis_stress",
          "Series must be a non-empty array of valid { t, v } points with no NaN values"
        )
      );
    }

    // Calculate average deviation from zero
    const sum = series.reduce((acc, point) => acc + point.v, 0);
    const avgDeviation = sum / series.length;

    const currentBasis = series[series.length - 1].v;
    const stressScore = deviationToStressScore(avgDeviation);
    const severity = determineSeverity(stressScore);

    return {
      stressScore,
      severity,
      avgDeviation: Math.round(avgDeviation * 100) / 100,
      currentBasis: Math.round(currentBasis * 100) / 100,
    };
  },
};
